import { useEffect, useState } from "react";
import { UserPlus, Loader2, Building2, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";

const AdminClients = () => {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", business_name: "", business_type: "", city: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API}/admin/hosts`, { headers: await authHeaders() });
      const data = await res.json();
      setHosts(data.hosts || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.business_name.trim()) {
      toast.error("Email and business name are required");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/hosts`, {
        method: "POST",
        headers: { ...(await authHeaders()), "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      toast.success(data.message || "Client registered");
      setForm({ email: "", business_name: "", business_type: "", city: "" });
      load();
    } catch (e) {
      toast.error(e.message || "Couldn't register client");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-clients-section">
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="w-5 h-5" />
        <h2 className="text-xl font-bold">B2B Clients</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Register a business on the spot. They'll land straight in the Host Hub when they sign up with this email.</p>

      <form onSubmit={create} className="grid sm:grid-cols-2 gap-3 bg-card border border-border rounded-xl p-5 mb-8">
        <div className="sm:col-span-2">
          <Label htmlFor="c-email">Client email *</Label>
          <Input id="c-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="owner@studio.com" className="mt-1" data-testid="admin-client-email" />
        </div>
        <div>
          <Label htmlFor="c-name">Business name *</Label>
          <Input id="c-name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
            placeholder="Flow Yoga Studio" className="mt-1" data-testid="admin-client-business" />
        </div>
        <div>
          <Label htmlFor="c-type">Business type</Label>
          <Input id="c-type" value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })}
            placeholder="Yoga studio" className="mt-1" data-testid="admin-client-type" />
        </div>
        <div>
          <Label htmlFor="c-city">City</Label>
          <Input id="c-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
            placeholder="London" className="mt-1" data-testid="admin-client-city" />
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={saving} className="gap-2 rounded-full" data-testid="admin-client-create">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Register client
          </Button>
        </div>
      </form>

      <h3 className="text-sm font-medium text-muted-foreground mb-2">Registered clients ({hosts.length})</h3>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : hosts.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6">No clients yet.</p>
      ) : (
        <div className="space-y-2" data-testid="admin-clients-list">
          {hosts.map((h, i) => (
            <div key={i} className="flex items-center justify-between bg-card border border-border rounded-lg px-4 py-3" data-testid={`admin-client-row-${i}`}>
              <div className="min-w-0">
                <p className="font-medium truncate">{h.business_name}</p>
                <p className="text-xs text-muted-foreground truncate">{h.email || "(linked account)"} · {h.business_type || "—"} · {h.city || "—"}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-xs px-2 py-0.5 rounded-full ${h.status === "active" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-amber-500/15 text-amber-700 dark:text-amber-300"}`}>
                  {h.status === "active" ? `${h.plan || ""} active` : "unsubscribed"}
                </span>
                {h.linked
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-500" title="Signed up" />
                  : <Clock className="w-4 h-4 text-amber-500" title="Invited — awaiting signup" />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminClients;
